import React, { useState } from 'react'
import { Col, message, Modal, Popover, Row, Skeleton, Table } from 'antd'
import { useCreateVirtualModule, useListAssetTypes, useListVirtualModules, useUpdateVirtualModule } from 'graphQLHooks/virtualAssetManagerHooks/virtualAssetManagerHooks'
import { cloneDeep, isEmpty } from 'lodash'
import ExpandMenuIcon from 'components/ui/general/icons/ExpandMenuIcon'
import AddEditVirtualManagerModal from 'components/ui/dataEntry/other/AddEditVirtualModuleModal'
import Button from 'components/ui/general/buttons/Button'
import { utilityService } from 'services/UtilityService'
import './VirtualAssetManagerDetails.scss'
import { getAssetTypesNameArray } from 'components/ui/dataDisplay/utils'
import { ADD_VAM_ASSET_TYPE_DROPDOWN_KEY_TYPE } from 'constants/general-constants'

const actionMenuOptionTypes = {
  edit: {
    type: 'edit',
    name: 'Edit'
  },
  delete: {
    type: 'archive',
    name: 'Archive'
  }
}

const actionMenuOptions = [
  actionMenuOptionTypes.edit,
  actionMenuOptionTypes.delete
]

const TABLE_SCROLL_VALUE = { y: `calc(100vh - 280px)` }
const VirtualAssetManagerDetails = ({ project, refetchPermissions, onHistoryUpdate }) => {
  const [isEditMode, setIsEditMode] = useState(false)
  const [isAddEditVirtualManagerModalOpen, setIsAddEditVirtualManagerModalOpen] = useState(false)
  const [deviceTableToggleMenuItems, setDeviceTableToggleMenuItems] = useState({})
  const [selectedVirtualModuleRow, setSelectedVirtualModuleRow] = useState(null)
  const [deleteItemDetails, setDeleteItemDetails] = useState(null)
  const handleOpenCloseToggleMenu = (rowId, flag) => {
    setDeviceTableToggleMenuItems(prevState => {
      let currentData = cloneDeep(prevState)
      currentData[rowId] = flag
      return currentData
    })
  }
  const toggleActionMenu = (selectedOption, details) => {
    const isEditAction = selectedOption.type === actionMenuOptionTypes.edit.type
    const isDeleteAction = selectedOption.type === actionMenuOptionTypes.delete.type
    if (isEditAction) {
      setIsAddEditVirtualManagerModalOpen(true)
      setIsEditMode(true)
    } else if (isDeleteAction) {
      const assetTypeList = details?.assetTypes?.map(item => {
        return {
          [ADD_VAM_ASSET_TYPE_DROPDOWN_KEY_TYPE]: item
        }
      }) || []
      const assetTypesData = getAssetTypesNameArray(assetTypeList, ADD_VAM_ASSET_TYPE_DROPDOWN_KEY_TYPE)
      const assetTypes = isEmpty(assetTypesData) ? null : assetTypesData
      // when deleting just pass isArchived true, keeping rest of the data same
      const variables = {
        id: details.id,
        assetTypes,
        project,
        title: details.title,
        isActive: false,
        isArchived: true
      }
      updateVirtualModuleMutation({
        variables,
        onCompleted: () => {
          // refetch permissions so that module is added instantly in header menu
          setDeleteItemDetails(null)
          if (refetchPermissions) {
            refetchPermissions()
          }
          setTimeout(() => {
            refetchVirtualModuleList()
            message.success(`Archived ${details.title} successfully`)
          }, 1000)
          onHistoryUpdate()
        },
        onError: (error) => {
          utilityService.handleError(error)
        }
      })
    }
  }

  const {
    data: virtualModulesList,
    loading: virtualModulesListLoading,
    refetch: refetchVirtualModuleList
  } = useListVirtualModules({
    variables: {
      project
    }
  })

  const {
    data: assetList
    // loading: isAssetListLoading
  } = useListAssetTypes({
    variables: {
      limit: 500,
      nextToken: null,
      filter: null,
      project
    }
  })

  const [
    createVirtualModuleMutation,
    {
      loading: isCreateVirtualModuleLoading
    }
  ] = useCreateVirtualModule()

  const [
    updateVirtualModuleMutation,
    {
      loading: isUpdateVirtualModuleLoading
    }
  ] = useUpdateVirtualModule()

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title'
    },
    {
      title: 'Asset Types',
      dataIndex: 'assetTypes',
      render: (assetTypes) => {
        const assetTypesString = assetTypes?.join(', ') || ''
        return (
          <span>{assetTypesString}</span>
        )
      }
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      render: (isActive) => {
        return (
          <span>{isActive ? 'Yes' : 'No'}</span>
        )
      }
    },
    {
      title: '',
      key: 'action',
      render: (details) => {
        const rowId = details?.id
        return (
          <div className='option-list' id='option-list-container'>
            <Popover
              content={
                actionMenuOptions.map(item => {
                  const isDeleteAction = item.type === actionMenuOptionTypes.delete.type
                  if (isDeleteAction) {
                    return (
                      // <Popconfirm
                      //   title='Are you sure delete this module ?'
                      //   onConfirm={() => {
                      //     toggleActionMenu(item, details)
                      //     handleOpenCloseToggleMenu(rowId, false)
                      //   }}
                      //   onCancel={() => {
                      //     handleOpenCloseToggleMenu(rowId, false)
                      //   }}
                      //   okText='Yes'
                      //   cancelText='No'
                      // >
                      <p
                        onClick={() => {
                          setSelectedVirtualModuleRow(details)
                          setDeleteItemDetails({ details, item })
                          // close 3 dots option menu
                          handleOpenCloseToggleMenu(rowId, false)
                        }}
                      >
                        {item.name}
                      </p>
                      // </Popconfirm>
                    )
                  }
                  return (
                    <p
                      onClick={() => {
                        toggleActionMenu(item, details)
                        setSelectedVirtualModuleRow(details)
                        handleOpenCloseToggleMenu(rowId, false)
                      }}
                    >
                      {item.name}
                    </p>
                  )
                })
              }
              title={null}
              trigger='click'
              visible={deviceTableToggleMenuItems?.[rowId]}
              onVisibleChange={(flag) => handleOpenCloseToggleMenu(rowId, flag)}
              placement='left'
            >
              <span><ExpandMenuIcon /></span>
            </Popover>
          </div>
        )
      }
    }
  ]

  return (
    <div className='virtual-asset-manager-details'>
      <div className='admin-list placeholder-data' id='admin-list'>
        {
          virtualModulesListLoading
            ? <Skeleton active avatar={false} title paragraph={{ rows: 1 }} loading={virtualModulesListLoading} />
            : !isEmpty(virtualModulesList?.listVirtualModules?.items)
              ? (
                <Table
                  columns={columns}
                  dataSource={virtualModulesList?.listVirtualModules?.items}
                  pagination={false}
                  scroll={TABLE_SCROLL_VALUE}
                  className='enhance-table'
                />
              )
              : <p>No virtual modules added.</p>
        }
      </div>
      <Row className='add-virtual-module-wrapper'>
        <Col span={19} />
        <Col span={5} >
          <Button
            buttonText='Add Virtual Module'
            color='red'
            onClick={() => setIsAddEditVirtualManagerModalOpen(true)}
            style={{ width: '150px' }}
            className='add-virtual-module-button'
          // isDisabled={isDeleteDisabled}
          />
        </Col>
      </Row>
      <AddEditVirtualManagerModal
        visible={isAddEditVirtualManagerModalOpen}
        onOk={({ clearAllFields, ...variables }) => {
          if (isEditMode) {
            updateVirtualModuleMutation({
              variables,
              onCompleted: () => {
                // refetch permissions so that module is added instantly in header menu
                if (refetchPermissions) {
                  refetchPermissions()
                }
                setIsAddEditVirtualManagerModalOpen(false)
                setTimeout(() => {
                  message.success(`Updated ${variables.title} successfully`)
                  refetchVirtualModuleList()
                  clearAllFields()
                  setIsEditMode(false)
                }, 1000)
                onHistoryUpdate()
              },
              onError: (error) => {
                utilityService.handleError(error)
              }
            })
          } else {
            createVirtualModuleMutation({
              variables,
              onCompleted: () => {
                // refetch permissions so that module is added instantly in header menu
                if (refetchPermissions) {
                  refetchPermissions()
                }
                setIsAddEditVirtualManagerModalOpen(false)
                setTimeout(() => {
                  message.success(`Created ${variables.title} successfully`)
                  refetchVirtualModuleList()
                  clearAllFields()
                  setIsEditMode(false)
                }, 1000)
                onHistoryUpdate()
              },
              onError: (error) => {
                utilityService.handleError(error)
              }
            })
          }
        }}
        onCancel={() => {
          setIsAddEditVirtualManagerModalOpen(false)
          setTimeout(() => {
            setIsEditMode(false)
            setSelectedVirtualModuleRow(null)
          }, 500)
        }}
        confirmLoading={
          isEditMode
            ? isUpdateVirtualModuleLoading
            : isCreateVirtualModuleLoading
        }
        isEditMode={isEditMode}
        fieldData={selectedVirtualModuleRow}
        project={project}
        assetList={assetList?.listAssetTypes?.items || []}
      />
      <Modal
        title='ARCHIVE VIRTUAL ASSET MANAGER'
        visible={Boolean(deleteItemDetails)}
        onOk={() => {
          toggleActionMenu(deleteItemDetails.item, deleteItemDetails.details)
        }}
        onCancel={() => {
          setDeleteItemDetails(null)
        }}
        className='general-modal'
        okText='Archive'
        cancelText='Cancel'
        okButtonProps={{
          disabled: isUpdateVirtualModuleLoading
        }}
        confirmLoading={isUpdateVirtualModuleLoading}
        getContainer={document.getElementById('admin-list')}
        maskClosable={false}
      >
        <p>Are you sure you want to archive this module ?</p>
        {/* <p>This action cannot be undone.</p> */}
      </Modal>
    </div>
  )
}

export default VirtualAssetManagerDetails
